import {
  ScheduleOutlined,
  Person,
  CalendarMonthOutlined,
  Restaurant,
} from "@mui/icons-material";
import {
  Box,
  Button,
  ClickAwayListener,
  Fade,
  FormControl,
  MenuItem,
  Popper,
  Select,
  Typography,
} from "@mui/material";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import SelectableChip from "components/SelectableChip";
import TimezoneChip from "components/TimezoneChip";
import dayjs, { Dayjs } from "dayjs";
import { SelectDatePicker } from "features/homepage/components/SelectDatePicker";
import DatePickerButton from "features/searchStore/components/DatePickerButton";
import { generatePeopleNumberOpts } from "helpers/generatePeopleNumerOption";
import { generateTimeOptions } from "helpers/generateTimeOptions";
import { ReactNode, useRef, useState } from "react";

export function ReservedSection() {
  const todayDate = new Date();
  const pickerRef = useRef<HTMLDivElement | null>(null);

  const [dateValue, setDateValue] = useState<Dayjs | null>(dayjs(todayDate));
  const [timeValue, setTimeValue] = useState<string | null>("19:00");
  const [customerNumber, setCustomerNumber] = useState<number | null>(2);
  const [openCalendar, setOpenCalendar] = useState(false);

  const [filterValue, setFilterValue] = useState(0);

  const handleChipSelected = (value: any) => {
    setFilterValue(value);
  };

  return (
    <Box
      display="flex"
      flex={1}
      alignItems="center"
      justifyContent="center"
      width="100%"
      bgcolor="#eee"
    >
      <Box width="1000px" display="flex" flexDirection="column">
        <Typography variant="h5" fontWeight="bold" sx={{ my: 2 }}>
          予約できるプランを探す{" "}
        </Typography>

        <Box
          ref={pickerRef}
          display="flex"
          p={1}
          bgcolor="#ddd"
          alignItems="center"
        >
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
            <Box display="flex" flex={1} alignItems="center" px={1}>
              <Box
                display="flex"
                bgcolor="white"
                alignItems="center"
                p={1}
                width="100%"
                borderRadius={1}
                boxShadow={0.5}
                sx={{
                  cursor: "pointer",
                }}
                onClick={() => setOpenCalendar(true)}
              >
                <CalendarMonthOutlined
                  fontSize="small"
                  color="primary"
                  sx={{ mx: 1 }}
                />
                <Typography>{dateValue?.format("M月D日（ddd）")}</Typography>
              </Box>

              <Popper
                open={openCalendar}
                anchorEl={pickerRef.current}
                placement="bottom-start"
                transition
                disablePortal
                modifiers={[
                  {
                    name: "preventOverflow",
                    enabled: true,
                    options: {
                      altAxis: true,
                      altBoundary: true,
                      tether: true,
                      rootBoundary: "document",
                      padding: 8,
                    },
                  },
                ]}
                sx={{
                  position: "relative",
                  overflowX: "unset",
                  overflowY: "unset",
                  boxShadow: 1,
                  zIndex: 5,
                  borderTop: 1,
                  borderColor: "divider",
                }}
              >
                {({ TransitionProps }) => (
                  <ClickAwayListener onClickAway={() => setOpenCalendar(false)}>
                    <Fade {...TransitionProps} timeout={250}>
                      <Box
                        display="flex"
                        bgcolor="white"
                        zIndex={120}
                        width="100%"
                        maxHeight="500px"
                        flexWrap="wrap"
                      >
                        <DateCalendar
                          value={dateValue}
                          disablePast
                          onChange={(newValue) => setDateValue(newValue)}
                        />
                      </Box>
                    </Fade>
                  </ClickAwayListener>
                )}
              </Popper>
            </Box>
            <Box display="flex" flex={1} px={1}>
              <FormControl
                sx={{ minWidth: 120, my: 2, display: "flex" }}
                size="small"
                fullWidth
              >
                <Select
                  fullWidth
                  sx={{ bgcolor: "white" }}
                  value={timeValue}
                  onChange={(e) => setTimeValue(e.target.value)}
                  renderValue={(value: ReactNode) => {
                    return (
                      <Box sx={{ display: "flex", gap: 1, alignItems: "end" }}>
                        <ScheduleOutlined color="primary" fontSize="small" />
                        {value}
                      </Box>
                    );
                  }}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  <MenuItem value="指定なし">指定なし</MenuItem>
                  {generateTimeOptions(7, 24)}
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" flex={1} px={1}>
              <FormControl
                sx={{ minWidth: 120, my: 2, display: "flex" }}
                size="small"
                fullWidth
              >
                <Select
                  fullWidth
                  sx={{ bgcolor: "white" }}
                  value={customerNumber}
                  onChange={(e) => setCustomerNumber(e.target.value as number)}
                  MenuProps={{
                    PaperProps: {
                      style: {
                        maxHeight: 200,
                      },
                    },
                  }}
                >
                  {generatePeopleNumberOpts(1, 6)}
                </Select>
              </FormControl>
            </Box>

            <Button variant="contained" sx={{ py: 1, px: 6 }}>
              <Typography>検索する</Typography>
            </Button>
          </LocalizationProvider>
        </Box>

        <Box display="flex" width="100%">
          <SelectableChip
            value={0}
            label="すべて"
            onSelected={handleChipSelected}
            defaultState={filterValue === 0}
          />
          <SelectableChip
            value={1}
            label="ドリンク付き"
            onSelected={handleChipSelected}
            defaultState={filterValue === 1}
          />
          <SelectableChip
            value={2}
            label=" 食事のみ"
            onSelected={handleChipSelected}
            defaultState={filterValue === 2}
          />
          <SelectableChip
            value={3}
            label="割引あり"
            onSelected={handleChipSelected}
            defaultState={filterValue === 3}
          />
        </Box>

        <Box display="flex" flexDirection="column" py={3}>
          <Box display="flex" bgcolor="white" flexDirection="column" p={1} boxShadow={1} borderRadius={1}>
            <Box display="flex" my={1}>
              <Box height="100px" width="170px" component="img" src={"/images/store-detail/course1.avif"} />
              <Box display="flex" flexDirection="column" p={1}>
                <TimezoneChip value={2} />
                <Typography
                  fontWeight="bold"
                  variant="body1"
                  display="flex"
                  flex={1}
                  sx={{ my: 1 }}
                >
                  【歓燕】当店お薦めオリジナルシグネチャー料理全8品
                </Typography>
                <Box display="flex" color="primary.main">
                  <Restaurant fontSize="small" sx={{ mx: 1 }} />
                  <Typography>メニューをみる</Typography>
                </Box>
              </Box>
            </Box>

            <Box
              border={1}
              borderColor="divider"
              p={1}
              my={1}
              display="flex"
              alignItems="center"
              sx={{
                cursor: 'pointer',
                "&:hover": {
                    opacity: 0.5
                }
              }}
            >
              <Typography sx={{ display: "flex", flex: 1 }}>
                コース＋女性にチョコレート菓子のお土産
              </Typography>
              <Typography variant="h6" fontWeight="bold" color="#f66">
                36,300円
              </Typography>
              <Button
                variant="contained"
                sx={{
                  px: 6,
                  py: 1,
                  mx: 1,
                  bgcolor: "#f66",
                }}
              >
                詳細・予約
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
